import React, { useState } from 'react';
import './ConflictResolver.css';
import { MappingConflict } from '../../types/columnMappingReview';

interface ConflictResolverProps {
  conflicts: MappingConflict[];
  onResolve: (originalColumn: string, chosenMetadataName: string) => void;
  onClose: () => void;
}

const ConflictResolver: React.FC<ConflictResolverProps> = ({ conflicts, onResolve, onClose }) => {
  const [selectedResolutions, setSelectedResolutions] = useState<Record<string, string>>({});

  const handleSelect = (originalColumn: string, metadataName: string) => {
    setSelectedResolutions(prev => ({
      ...prev,
      [originalColumn]: metadataName
    }));
  };

  const handleResolve = (originalColumn: string) => {
    const chosen = selectedResolutions[originalColumn];
    if (chosen) {
      onResolve(originalColumn, chosen);
      // Remove from selected after resolution
      setSelectedResolutions(prev => {
        const next = { ...prev };
        delete next[originalColumn];
        return next;
      });
    }
  };

  const handleResolveAll = () => {
    Object.entries(selectedResolutions).forEach(([column, metadata]) => {
      onResolve(column, metadata);
    });
    setSelectedResolutions({});
  };

  if (conflicts.length === 0) {
    return (
      <div className="conflict-resolver">
        <div className="conflict-header">
          <h2>No Conflicts Found</h2>
          <button className="close-button" onClick={onClose}>✕</button>
        </div>
        <div className="no-conflicts">
          All column mappings are consistent across domains.
        </div>
      </div>
    );
  }

  return (
    <div className="conflict-resolver">
      <div className="conflict-header">
        <h2>Conflicting Mappings ({conflicts.length})</h2>
        <button className="close-button" onClick={onClose}>✕</button>
      </div>

      <div className="conflict-info">
        These columns have different mappings across domains. Select the correct mapping for each column.
      </div>

      <div className="conflicts-list">
        {conflicts.map((conflict) => (
          <div key={conflict.original_column} className="conflict-item">
            <div className="conflict-column">
              <span className="column-name">{conflict.original_column}</span>
              <span className="conflict-count">{conflict.conflict_count} different mappings</span>
            </div>

            <div className="conflict-options">
              {conflict.mappings.map((mapping) => (
                <div 
                  key={mapping.id} 
                  className={`mapping-option ${selectedResolutions[conflict.original_column] === mapping.metadata_name ? 'selected' : ''}`}
                  onClick={() => handleSelect(conflict.original_column, mapping.metadata_name)}
                >
                  <div className="option-header">
                    <input 
                      type="radio" 
                      name={`conflict-${conflict.original_column}`}
                      checked={selectedResolutions[conflict.original_column] === mapping.metadata_name}
                      onChange={() => handleSelect(conflict.original_column, mapping.metadata_name)}
                    />
                    <span className="metadata-name">{mapping.metadata_name}</span>
                  </div>
                  <div className="option-details">
                    <span className="domain">Domain: {mapping.domain}</span>
                    <span className="usage">Used {mapping.usage_count} times</span>
                    {mapping.last_used && (
                      <span className="last-used">
                        Last: {new Date(mapping.last_used).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {selectedResolutions[conflict.original_column] && (
              <button 
                className="resolve-button"
                onClick={() => handleResolve(conflict.original_column)}
              >
                Apply Resolution
              </button>
            )}
          </div>
        ))}
      </div>

      {Object.keys(selectedResolutions).length > 0 && (
        <div className="conflict-actions">
          <button 
            className="resolve-all-button"
            onClick={handleResolveAll}
          >
            Apply All Selected Resolutions ({Object.keys(selectedResolutions).length})
          </button>
        </div>
      )}
    </div>
  );
};

export default ConflictResolver;